import { rest } from "@js/lib/network"

import { hasAdvertisingConsent } from "./consent"
import type { PixelEventData, PixelMap } from "./types"

const queuedCapiPixels = new Map<
  string,
  { data: PixelEventData; pixel: string }
>()
let capiDispatching = false
let inFlightCapiPixelKeys = new Set<string>()

export function dispatchCapi(pixels: PixelMap) {
  const pendingPixels = Object.entries(pixels).filter(
    ([, data]) => !data._conversion_api_dispatched,
  )
  if (pendingPixels.length === 0) {
    return
  }

  queueCapiPixels(pendingPixels)
  if (capiDispatching) {
    return
  }

  flushCapiQueue()
}

export function resetCapiForTests() {
  queuedCapiPixels.clear()
  capiDispatching = false
  inFlightCapiPixelKeys.clear()
}

function queueCapiPixels(pendingPixels: [string, PixelEventData][]) {
  pendingPixels.forEach(([pixel, data]) => {
    const key = capiPixelKey(pixel, data)
    if (inFlightCapiPixelKeys.has(key)) {
      return
    }

    queuedCapiPixels.set(key, { pixel, data })
  })
}

function flushCapiQueue() {
  if (capiDispatching) {
    return
  }

  const pendingPixels = Array.from(queuedCapiPixels.entries()).filter(
    ([, { data }]) => !data._conversion_api_dispatched,
  )
  if (pendingPixels.length === 0) {
    queuedCapiPixels.clear()
    return
  }

  pendingPixels.forEach(([key]) => queuedCapiPixels.delete(key))
  inFlightCapiPixelKeys = new Set(pendingPixels.map(([key]) => key))
  capiDispatching = true
  void rest
    .postJSON(
      `/capi?advertising_consent=${hasAdvertisingConsent() ? "granted" : "denied"}`,
      {
        pixels: Object.fromEntries(
          pendingPixels.map(([, { pixel, data }]) => [pixel, { ...data }]),
        ),
      },
      { keepalive: true },
    )
    .then((response) => {
      if (!response.ok) {
        return
      }

      pendingPixels.forEach(([, { data }]) => {
        data._conversion_api_dispatched = true
      })
    })
    .catch(() => {})
    .then(() => {
      capiDispatching = false
      inFlightCapiPixelKeys.clear()
      flushCapiQueue()
    })
}

function capiPixelKey(pixel: string, data: PixelEventData) {
  return `${pixel}:${data.event_id}`
}
